import { type DependencyList, type EffectCallback, useEffect, useRef } from 'react';

import { isDefined } from '@enterprise/core';

import { useDeepCompareEffect } from '#misc/compare/deep-compare-effect.hook';

import { useFirstMount } from './first-mount.hook';

/**
 * React hook that invokes a callback function when the component updates.
 *
 * @param callback - A function to be called when the component updates.
 * @param dependencies - An array of dependencies that trigger the callback when changed.
 *
 * @remarks
 * The callback is invoked after the first mount, and on subsequent updates,
 * ensuring that the most recent version of the callback is executed.
 *
 * @example
 * ```tsx
 * useDidUpdate(() => {
 *   // Logic to run on update
 * }, [dependency]);
 * ```
 */
export const useDidUpdate = (callback: EffectCallback, dependencies: DependencyList): void => {
  const ref = useRef<EffectCallback>(undefined);
  const isFirstMount = useFirstMount();

  useEffect(() => {
    ref.current = callback;
  }, [callback]);

  useDeepCompareEffect(() => {
    if (isFirstMount) {
      return;
    }

    const cb = ref.current;

    if (isDefined(cb)) {
      cb();
    }
    // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies passed directly to useDeepCompareEffect
  }, dependencies);
};
